import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import Loading from "../../components/Loading";

const OPTIONS = [
  { id: "scoring", label: "🎯 Scoring", hint: "Mehr Punkte auf der Runde" },
  { id: "finishing", label: "🎯 Finishing", hint: "Doppel und Checkouts" },
  { id: "mental", label: "🧠 Mental", hint: "Ruhe unter Druck" },
  { id: "recovery", label: "😌 Erholung", hint: "Form schonen" },
];

const ManagerTraining = () => {
  const { userId, manager, loading, refresh, setManager } = useVirtualWorld();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState("finishing");
  const guided = Boolean(manager?.guided) || (manager?.club?.friendlyPlayedAt && !manager?.club?.firstTrainingDoneAt);
  const phase = Number(manager?.onboardingPhase || manager?.progression?.manager?.onboardingPhase || 1);
  const comps = manager?.competitions || manager?.progression?.manager?.competitions || {};
  const leagueStarted = Boolean(comps.leagueStarted);
  const worldReady = Number(comps.worldReady || 0);
  const worldNeeded = Number(comps.worldNeeded || 8);
  const afterFirstTraining = Boolean(manager?.club?.firstTrainingDoneAt) && Number(manager?.club?.played || 0) < 1;
  const trainingPending = !manager?.club?.firstTrainingDoneAt && Number(manager?.club?.played || 0) < 1;

  const focus = useMemo(() => {
    const report = manager?.lastReport?.focusPlayer;
    if (report) return report;
    const players = (manager?.players || []).filter((p) => !p.loanedOut);
    const weak = [...players].sort((a, b) => Number(a.finishing || 0) - Number(b.finishing || 0))[0];
    if (!weak) return null;
    return {
      name: weak.name,
      playerKey: weak.playerKey,
      suggestion: "finishing",
      note: "Checkout-Darts gezielt trainieren",
    };
  }, [manager]);

  if (loading) return <Loading />;
  if (!manager?.club) return <p>Bitte zuerst einen Club gründen.</p>;

  const applyHome = (data) => {
    if (!data?.club) return null;
    setManager(data);
    const next =
      data?.guide?.current ||
      data?.progression?.manager?.guide?.current ||
      data?.progression?.manager?.next ||
      null;
    if (next?.to && next.to !== "/manager/training") {
      navigate(next.to);
    }
    return next;
  };

  const setOne = async (playerKey, training) => {
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await vw.setTraining(userId, [{ playerKey, training }]);
      const next = applyHome(res?.data);
      if (!res?.data?.club) await refresh();
      setMsg(next?.title ? `Gespeichert — weiter: ${next.title}` : "Training gespeichert.");
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const applyAll = async () => {
    if (busy) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await vw.setTraining(userId, [{ training: all }], true);
      const next = applyHome(res?.data);
      if (!res?.data?.club) await refresh();
      setMsg(
        next?.title
          ? `Training übernommen — weiter: ${next.title}`
          : leagueStarted
            ? "Training für alle übernommen. Als Nächstes wartet das Ligaspiel."
            : "Training für alle übernommen."
      );
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const applyFocus = async () => {
    if (!focus?.playerKey || busy) return;
    setBusy(true);
    setMsg("");
    try {
      const res = await vw.setTraining(userId, [
        { playerKey: focus.playerKey, training: focus.suggestion || "finishing" },
      ]);
      const next = applyHome(res?.data);
      if (!res?.data?.club) await refresh();
      setMsg(
        next?.title
          ? `${focus.name} trainiert Finishing — weiter: ${next.title}`
          : `${focus.name} trainiert jetzt Finishing.`
      );
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const showNextCard = afterFirstTraining || (guided && phase >= 3);

  return (
    <div className="space-y-5">
      {guided && trainingPending ? (
        <div className="rounded-3xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-5">
          <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">Schritt · Nach dem Match</div>
          <h2 className="text-3xl font-black mb-2">Erste Trainingsentscheidung</h2>
          <p className="opacity-80 mb-4">
            {focus
              ? `${focus.name} hat heute Chancen auf Doppel liegen gelassen. Willst du genau dort ansetzen?`
              : "Du hast das Team live gesehen — jetzt gezielt nacharbeiten."}
          </p>
          {focus ? (
            <button
              type="button"
              disabled={busy}
              onClick={applyFocus}
              className="px-5 py-3 rounded-xl bg-amber-700 text-amber-50 font-semibold mr-3 disabled:opacity-60"
            >
              Mit {focus.name} am Finishing arbeiten
            </button>
          ) : null}
        </div>
      ) : null}

      {showNextCard && leagueStarted ? (
        <div className="rounded-3xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-5">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Weiter begleitet</div>
          <h2 className="text-2xl font-black mb-2">Die reguläre Liga ist dran</h2>
          <p className="opacity-80 mb-4">
            Training sitzt. Als Nächstes bestreitest du dein erstes Divisionsspiel — ohne Inbox, Büro und Transferchaos.
          </p>
          <Link to="/manager/match" className="inline-flex px-5 py-3 rounded-xl bg-emerald-700 text-white font-semibold mr-3">
            Zum Ligaspiel
          </Link>
          <Link
            to="/manager/league?next=match"
            className="inline-flex px-5 py-3 rounded-xl bg-white dark:bg-gray-700 font-semibold border"
          >
            Kurz Tabelle ansehen
          </Link>
        </div>
      ) : null}

      {showNextCard && !leagueStarted ? (
        <div className="rounded-3xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-5">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Weiter begleitet</div>
          <h2 className="text-2xl font-black mb-2">Welt wächst — Liga kommt danach</h2>
          <p className="opacity-80 mb-4">
            Training sitzt. Die reguläre Liga startet bei {worldNeeded} Vereinen
            {worldReady ? ` (aktuell ${worldReady}/${worldNeeded})` : ""}. Bis dahin spielst du weiter Freundschaftsspiele
            und kannst Freunde einladen.
          </p>
          <Link to="/manager/match" className="inline-flex px-5 py-3 rounded-xl bg-emerald-700 text-white font-semibold mr-3">
            Weiter Freundschaftsspiel
          </Link>
          <Link to="/manager" className="inline-flex px-5 py-3 rounded-xl bg-white dark:bg-gray-700 font-semibold border mr-3">
            Freunde einladen
          </Link>
          <Link to="/manager/league" className="inline-flex px-5 py-3 rounded-xl bg-white dark:bg-gray-700 font-semibold border">
            Ligastatus ansehen
          </Link>
        </div>
      ) : null}

      <div className="flex gap-3 items-center flex-wrap">
        <select className="p-2 rounded text-black" value={all} onChange={(e) => setAll(e.target.value)} disabled={busy}>
          {OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy}
          onClick={applyAll}
          className="px-4 py-2 bg-gray-900 text-white rounded-xl disabled:opacity-60"
        >
          Für alle übernehmen
        </button>
      </div>
      {msg ? <p className="font-semibold">{msg}</p> : null}
      <div className="space-y-3">
        {(manager.players || []).map((p) => (
          <div key={p.playerKey} className="bg-white dark:bg-gray-700 p-4 rounded-2xl flex flex-wrap justify-between items-center gap-3 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={p.portraitUrl || p.avatar || `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(p.playerKey || p.name)}`}
                alt=""
                className="w-12 h-12 rounded-xl object-cover bg-amber-100"
              />
              <div>
                <div className="font-bold">{p.name}</div>
                <div className="text-sm opacity-70">{p.scoring}/{p.finishing}/{p.nerves} · {p.formLabel}</div>
              </div>
            </div>
            <select
              className="p-2 rounded text-black"
              disabled={busy}
              value={p.training || "recovery"}
              onChange={(e) => setOne(p.playerKey, e.target.value)}
            >
              {OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      {manager.club.firstTrainingDoneAt || manager.club.played > 0 ? (
        <p className="text-sm opacity-80">
          {leagueStarted ? (
            Number(manager.club.played || 0) < 1 ? (
              <>Als Nächstes: <Link className="underline font-semibold" to="/manager/match">erstes Ligaspiel</Link>.</>
            ) : (
              <>Weiter unter <Link className="underline font-semibold" to="/manager/match">Wettbewerbe</Link>.</>
            )
          ) : (
            <>Bis zum Ligastart: <Link className="underline font-semibold" to="/manager/match">Freundschaftsspiele</Link> oder{" "}
              <Link className="underline font-semibold" to="/manager">Freunde einladen</Link>.</>
          )}
        </p>
      ) : null}
    </div>
  );
};

export default ManagerTraining;
